import { Button } from '@/components/ui/button';
import { WebBreadcrumb } from '@/components/web/layouts/web-breadcrumb';
import { type BreadcrumbItem } from '@/types';
import { type Site } from '@/types/search.d';
import { Heart, Star } from 'lucide-react';
import { useState } from 'react';

interface DetailHeaderProps {
  site: Site;
  rating?: number;
  reviewCount?: number;
  bookingCount?: number;
  tag?: string;
  onFavorite?: () => void;
}

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M+`;
  if (n >= 1000) return `${Math.round(n / 1000)}K+`;
  return String(n);
}

export function DetailHeader({ site, rating = 4.5, reviewCount, bookingCount, tag, onFavorite }: DetailHeaderProps) {
  const [favorited, setFavorited] = useState(false);

  const breadcrumbItems: BreadcrumbItem[] = [
    ...(site.province ? [{ title: site.province, href: '#' }] : []),
    ...(site.city ? [{ title: site.city, href: '#' }] : []),
    { title: site.name },
  ];

  const handleFavorite = () => {
    setFavorited((prev) => !prev);
    onFavorite?.();
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Breadcrumb */}
      <WebBreadcrumb items={breadcrumbItems} className="px-0 text-xs" />

      {/* Title row */}
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl lg:text-5xl">{site.name}</h1>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 shrink-0 gap-1.5"
          onClick={handleFavorite}
          aria-label={favorited ? 'Bỏ yêu thích' : 'Yêu thích'}
        >
          <Heart className={`h-4 w-4 ${favorited ? 'fill-red-500 text-red-500' : ''}`} />
          <span className="hidden sm:inline">{favorited ? 'Đã thích' : 'Yêu thích'}</span>
        </Button>
      </div>

      {/* Subline */}
      {site.description && <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">{site.description}</p>}

      {/* Rating row */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-gray-600">
        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        <span className="font-medium">{rating.toFixed(1)}/5</span>
        {reviewCount !== undefined && reviewCount > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span>{formatCount(reviewCount)} đánh giá</span>
          </>
        )}
        {bookingCount !== undefined && bookingCount > 0 && (
          <>
            <span className="text-gray-400">·</span>
            <span>{formatCount(bookingCount)} đã đặt</span>
          </>
        )}
        {tag && (
          <>
            <span className="text-gray-400">·</span>
            <span
              className="rounded-full border px-2 py-0.5 text-xs"
              style={{
                backgroundColor: 'var(--pill-bg)',
                borderColor: 'var(--pill-border)',
              }}
            >
              {tag}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
